/**
 * VivaDecor – price of the selected size above the Add to cart button.
 * Re-reads the selected variant (hidden input name="id") and the quantity after every
 * size or quantity change. Prices come from Liquid (minor units); nothing is hardcoded here.
 */

/**
 * Builds a money formatter from a sample rendered by Liquid's money filter (123456789 minor units).
 * @param {string} sample
 * @returns {(cents: number) => string}
 */
function vpaMoneyFormatter(sample) {
  const match = (sample || '').match(/\d[\d.,'\s  ]*\d/);

  if (!match) {
    return (cents) => (cents / 100).toFixed(2);
  }

  const number = match[0];
  const prefix = sample.slice(0, match.index);
  const suffix = sample.slice((match.index || 0) + number.length);
  const separators = number.match(/[^\d]/g) || [];
  const groups = number.split(/[^\d]/);
  const lastGroup = groups[groups.length - 1] || '';

  let decimals = 0;
  let decimalSeparator = '';
  let thousandsSeparator = '';

  if (groups.length > 1 && lastGroup.length === 2) {
    decimals = 2;
    decimalSeparator = separators[separators.length - 1] || '.';
    thousandsSeparator = separators.length > 1 ? separators[0] || '' : '';
  } else {
    thousandsSeparator = separators[0] || '';
  }

  return (cents) => {
    const fixed = (Math.round(cents) / 100).toFixed(decimals);
    const parts = fixed.split('.');
    const whole = (parts[0] || '0').replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator);

    return prefix + (decimals > 0 ? whole + decimalSeparator + (parts[1] || '00') : whole) + suffix;
  };
}

class VdPriceAtc extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {Record<string, {p: number, c: number, t: string}>} */
  #variants = {};

  /** @type {(cents: number) => string} */
  #formatMoney = (cents) => (cents / 100).toFixed(2);

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    try {
      this.#variants = JSON.parse(this.querySelector('script[data-vpa-variants]')?.textContent || '{}');
    } catch (error) {
      this.#variants = {};
    }

    this.#formatMoney = vpaMoneyFormatter(this.dataset.moneySample || '');

    const form = this.closest('product-form-component');
    form?.addEventListener('change', this.#schedule, { signal });
    form?.addEventListener('input', this.#schedule, { signal });
    form?.addEventListener('click', this.#schedule, { signal });
    document.addEventListener('quantity-selector:update', this.#schedule, { signal });

    const section = this.closest('.shopify-section, dialog, product-card');
    section?.addEventListener('variant:update', this.#schedule, { signal });

    this.#render();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
    if (this.#timeout) clearTimeout(this.#timeout);
  }

  /** Runs after all other listeners (product-form.js sets the new variant id in its own listener). */
  #schedule = () => {
    if (this.#timeout) clearTimeout(this.#timeout);
    this.#timeout = setTimeout(() => this.#render(), 0);
  };

  #render() {
    const form = this.closest('product-form-component');
    const idInput = /** @type {HTMLInputElement | null} */ (form?.querySelector('input[name="id"]') ?? null);
    const variant = idInput ? this.#variants[idInput.value] : undefined;
    if (!variant) return;

    const qtyInput = /** @type {HTMLInputElement | null} */ (form?.querySelector('input[name="quantity"]') ?? null);
    const parsed = parseInt(qtyInput?.value || '1', 10);
    const qty = Number.isFinite(parsed) && parsed > 0 ? parsed : 1;

    const label = this.querySelector('[data-vpa-label]');
    if (label) {
      label.textContent =
        qty > 1 ? (this.dataset.labelTotal || '').replace('[qty]', String(qty)) : this.dataset.label || '';
    }

    const price = this.querySelector('[data-vpa-price]');
    if (price) price.textContent = this.#formatMoney(variant.p * qty);

    const regular = this.querySelector('[data-vpa-regular]');
    const compare = this.querySelector('[data-vpa-compare]');
    if (regular instanceof HTMLElement && compare) {
      const onSale = variant.c > variant.p;
      regular.hidden = !onSale;
      compare.textContent = onSale ? this.#formatMoney(variant.c * qty) : '';
    }

    const title = this.querySelector('[data-vpa-variant]');
    if (title) title.textContent = variant.t;
  }
}

if (!customElements.get('vd-price-atc')) {
  customElements.define('vd-price-atc', VdPriceAtc);
}
